import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { SpreadInfo } from 'editor/src/store/design/types';

import { RulerGuide } from './geometry';

const EMPTY_GUIDES: RulerGuide[] = [];
type GuideGeometry = Omit<SpreadInfo, 'spreadIndex'>;
const RulerGuidesContext = createContext<{
  guides: RulerGuide[];
  geometry?: GuideGeometry;
  setGuides: (guides: RulerGuide[]) => void;
}>({
  guides: EMPTY_GUIDES,
  setGuides: () => {},
});

// Editing aids deliberately live outside designData, so they cannot enter print exports.
export function RulerGuidesProvider({
  scope,
  children,
  geometry,
  enabled = true,
}: {
  scope: string;
  children: React.ReactNode;
  geometry?: GuideGeometry;
  enabled?: boolean;
}) {
  const [byScope, setByScope] = useState<Record<string, RulerGuide[]>>({});
  useEffect(() => {
    if (!enabled || !geometry || geometry.spreadWidth <= 0 || geometry.spreadHeight <= 0) {
      return;
    }
    setByScope((current) =>
      current[scope]?.length
        ? current
        : {
            ...current,
            [scope]: [
              { id: 1, axis: 'horizontal', position: 0 },
              { id: 2, axis: 'vertical', position: 0 },
            ],
          },
    );
  }, [enabled, scope, geometry?.spreadWidth, geometry?.spreadHeight]);
  const guides = (enabled && byScope[scope]) || EMPTY_GUIDES;
  const value = useMemo(
    () => ({
      guides,
      geometry,
      setGuides: (next: RulerGuide[]) => setByScope((current) => ({ ...current, [scope]: next })),
    }),
    [guides, scope, geometry?.spreadCoords, geometry?.spreadWidth, geometry?.spreadHeight],
  );
  return <RulerGuidesContext.Provider value={value}>{children}</RulerGuidesContext.Provider>;
}

export default function useRulerGuides() {
  return useContext(RulerGuidesContext);
}
